import { Component, DestroyRef, Input, OnInit, computed, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { ShopService } from '@app/services/shops/shop.service';
import { SmartCartPreviewStateService } from '@pages/engage/smart-cart/smart-cart-form/preview/smart-cart-preview-state.service';
import { currencyCodes } from '@pdqdev/pdq-shared/dist/utils/currency-utils';
import {
  GOAL_NOT_MET_DEFAULTS,
  GoalFormShape,
  MinimumThresholdType,
  buildGoalThresholdGroup,
} from '../../../goals-progress-bar.model';

@Component({
  selector: 'smart-cart-goal-threshold-tab',
  templateUrl: './goal-threshold-tab.component.html',
  styleUrls: ['../../../../../components/shared.scss'],
  standalone: false,
})
export class SmartCartGoalThresholdTabComponent implements OnInit {
  @Input({ required: true }) goal!: FormGroup<GoalFormShape>;
  @Input({ required: true }) index!: number;
  @Input({ required: true }) minimumThresholdType!: FormControl<MinimumThresholdType>;
  @Input() thresholdErrors: Record<string, string> | null = null;

  private readonly destroyRef = inject(DestroyRef);
  private readonly previewState = inject(SmartCartPreviewStateService);
  private readonly shopService = inject(ShopService);

  readonly currencyCodes = currencyCodes;
  readonly notMetDefaults = GOAL_NOT_MET_DEFAULTS;

  defaultCountry = 'US';
  defaultCurrency = 'USD';

  readonly previewingMet = computed(
    () => this.previewState.goalsPreviewGoalIndex() === this.index && this.previewState.goalsPreviewMet(),
  );
  readonly previewingNotMet = computed(
    () => this.previewState.goalsPreviewGoalIndex() === this.index && !this.previewState.goalsPreviewMet(),
  );

  ngOnInit(): void {
    this.defaultCurrency = this.shopService.shopCurrency ?? 'USD';
    this.shopService.primaryLocation$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((location) => {
      this.defaultCountry = location?.countryCode ?? 'US';
    });

    this.minimumThresholdType.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((type) => this.applyNotMetMessageDefault(type));
  }

  previewMet(met: boolean): void {
    this.previewState.setGoalsPreview(this.index, met);
  }

  addThreshold(): void {
    this.goal.controls.thresholds.push(buildGoalThresholdGroup(this.defaultCountry, this.defaultCurrency));
  }

  removeThreshold(thresholdIndex: number): void {
    this.goal.controls.thresholds.removeAt(thresholdIndex);
  }

  onMessageWhenNotMetChange(html: string): void {
    this.goal.controls.messageWhenNotMet.setValue(html);
  }

  onMessageWhenMetChange(html: string): void {
    this.goal.controls.messageWhenMet.setValue(html);
  }

  // Refresh the "goal not met" default when the threshold type changes and the message is still a
  // default (empty or either type's default) — leaving a customized message untouched.
  private applyNotMetMessageDefault(type: MinimumThresholdType): void {
    const newDefault = GOAL_NOT_MET_DEFAULTS[type];
    const knownDefaults = [GOAL_NOT_MET_DEFAULTS.cart_value, GOAL_NOT_MET_DEFAULTS.item_count].map((message) =>
      this.toPlainText(message),
    );

    const control = this.goal.controls.messageWhenNotMet;
    const current = this.toPlainText(control.value);
    if (current === '' || knownDefaults.includes(current)) {
      control.setValue(newDefault);
    }
  }

  private toPlainText(value: string): string {
    return (value ?? '')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }
}
